import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as CloseIcon } from 'assets/icons/notificationClose.svg';
import { ADMIN_ROLES, messageTemplatesIds, pageIds } from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import { NotificationResult, useNotifications } from 'hooks/useNotifications';

import { Notification } from '../../../dashboard/interfaces/notifications.interfaces';
import {
  filterNoToastNotification,
  groupNotificationsByFormattedDate,
  relativeDate,
  relativeDateForGroup,
} from 'utilities/functions';
import {
  User_Notification_Status_Enum,
  useUpdateAllUserNotificationStatusMutation,
} from 'graphql/generated/hasura';
import { useNavigate } from 'react-router-dom';
import { useGetMessageTemplates } from 'hooks/useGetMessageTemplates';
import ButtonComponent from 'components/button/buttonComponent';
import { twMerge } from 'tailwind-merge';
import Loader from 'components/loaderComponent';
import { useNotificationCount } from 'notifications/contexts/NotificationsContext';
import { NotificationPlaceholder } from './components/NotificationPlaceholder';
import { NotificationGroup } from './components/NotificationGroup';
import { NotificationTab } from './components/NotificationTab';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import Spinner from '../../../../components/Spinner';
import { useAuth } from '../../../../auth/context/AuthContext';

const NOTIFICATION_TAB = {
  ALL: 'all',
  UNREAD: 'unread',
};

const NOTIFICATION_STATUS_BY_TAB = {
  [NOTIFICATION_TAB.UNREAD]: [User_Notification_Status_Enum.Delivered],
};

const MAX_COUNT = 99;
const MAXED_COUNT_TEXT = `${MAX_COUNT}+`;

const LIMIT_STEP = 20;

export const NotificationNavbarDropdown = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.NOTIFICATIONS,
  });
  const navigate = useNavigate();
  const { user, selectedAccount } = useAuth();
  const [activeTab, setActiveTab] = useState<string>(NOTIFICATION_TAB.UNREAD);
  const [limit, setLimit] = useState<number>(LIMIT_STEP);

  const isAdmin = useMemo(
    () => !!user?.role && ADMIN_ROLES.includes(user?.role),
    [user],
  );

  const variables = useMemo(
    () => ({
      includeStatuses: NOTIFICATION_STATUS_BY_TAB[activeTab],
      offset: 0,
      limit: limit,
      userId: selectedAccount,
    }),
    [activeTab, limit, selectedAccount],
  );

  const {
    loading: loadingNotifications,
    notifications: notificationsData,
    loadMoreNotifications,
    hasMore,
    unreadCount: unreadCountData,
    updateAllNotifications,
  } = useNotifications(variables);

  const { internalNotificationCount } = useNotificationCount();

  const countByTab = useMemo(() => {
    return {
      [NOTIFICATION_TAB.UNREAD]: unreadCountData ?? internalNotificationCount,
    };
  }, [unreadCountData, internalNotificationCount]);

  const loaderRef = useInfiniteScroll(() => {
    if (!loading && hasMore) {
      loadMoreNotifications();
    }
  });

  const [
    updateAllUserNotificationStatus,
    { loading: loadingUpdateAllNotificationStatus },
  ] = useUpdateAllUserNotificationStatusMutation({
    variables: {
      status: User_Notification_Status_Enum.Read,
    },
    onCompleted: () => {
      return updateAllNotifications();
    },
  });

  const { data: messageLocale, loading: messageLoading } =
    useGetMessageTemplates({
      locale: 'en',
      templateId: [
        messageTemplatesIds.MY_PROFILE,
        messageTemplatesIds.MY_PERSONAL_INFO,
        messageTemplatesIds.MY_CONTACT_INFO,
        messageTemplatesIds.HEALTH_QUESTIONNAIRE,
      ],
    });

  const [notifications, setNotifications] = useState<Notification[]>([]);

  const grouped = groupNotificationsByFormattedDate(notifications);

  const populateNotificationsData = useCallback(
    (notifications: NotificationResult) => {
      const populateNotifications: Notification[] =
        notifications.notifications.map((notification) => {
          return {
            id: notification.id,
            title: notification.data.title,
            message: notification.data.message,
            groupRelativeDate: relativeDateForGroup(notification.data.date),
            date: relativeDate(notification.data.date),
            originalDate: notification.data.date,
            isRead: notification.status === User_Notification_Status_Enum.Read,
            action: notification.data.action,
            extraActions: notification.data.extraActions,
            constants: notification.data.constants,
            activityType: notification.activity_type,
            is_toast: notification.is_toast || false,
          };
        }) ?? [];

      setNotifications(filterNoToastNotification(populateNotifications));
    },
    [],
  );

  useEffect(() => {
    if (notificationsData?.notifications?.length) {
      populateNotificationsData(notificationsData);
    } else {
      setNotifications([]);
    }
  }, [populateNotificationsData, notificationsData, navigate]);

  if (
    (loading && !locale) ||
    loadingNotifications ||
    !messageLocale ||
    messageLoading
  )
    return (
      <div
        className={twMerge('max-w-full w-[630px] py-1', !isOpen && 'hidden')}
      >
        <Loader testID={'notifications-loading'} />
      </div>
    );

  return (
    <div
      className={twMerge(
        'max-w-full w-[630px] max-h-[70vh]',
        !isOpen && 'hidden',
      )}
      data-testid="notifications-dropdown"
    >
      <div className="h-full">
        <div className="w-full flex items-center justify-between py-5 px-[30px]">
          <h3 className="w-full flex text-h4 text-btn-black font-semibold">
            {locale.title}
          </h3>

          <div
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
            className="cursor-pointer"
            data-testid="close-notifications"
          >
            <CloseIcon className="w-5 h-5 fill-current" />
          </div>
        </div>

        <hr />

        <div className="w-full flex items-center justify-between py-5 px-30">
          <div className="flex gap-2.5 items-center">
            {Object.values(NOTIFICATION_TAB).map((tab) => {
              const count = isAdmin ? 0 : countByTab[tab];

              return (
                <NotificationTab
                  onClick={() => {
                    setLimit(LIMIT_STEP);
                    setActiveTab(tab);
                  }}
                  isActive={activeTab === tab}
                  tab={tab}
                  count={count > MAX_COUNT ? MAXED_COUNT_TEXT : count}
                />
              );
            })}
          </div>

          <ButtonComponent
            type={'underline'}
            testID={'mark-all-as-read-button'}
            className={twMerge(!unreadCountData && 'hidden')}
            onClick={() => updateAllUserNotificationStatus()}
            disabled={loadingUpdateAllNotificationStatus}
            showSpinner={loadingUpdateAllNotificationStatus}
          >
            {'Mark all as read'}
          </ButtonComponent>
        </div>

        <hr />

        <div className="overflow-y-scroll overflow-x-hidden max-h-[calc(70vh-150px)] pb-[70px]">
          {grouped.length ? (
            grouped.map((item, index) => (
              <React.Fragment key={item.label}>
                <NotificationGroup
                  {...item}
                  onCloseModal={onClose}
                  refreshNotification={updateAllNotifications}
                />
                {index < grouped.length - 1 ? (
                  <hr />
                ) : (
                  hasMore && (
                    <div ref={loaderRef} className="py-2.5 flex justify-center">
                      <Spinner />
                    </div>
                  )
                )}
              </React.Fragment>
            ))
          ) : (
            <NotificationPlaceholder />
          )}
        </div>
      </div>
    </div>
  );
};
